import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import MaterialTable from '@material-table/core';
import flowRight from 'lodash.flowright';

import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import { actions as reduxActions } from '../reducers';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import tableColumns from './tableColumns';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import getCdrHooks from '../actions/getCdrHooks';
import CustomTableTitle from '../../../partials/CustomTableTitle';
import { translate } from '../../../i18n/I18nProvider';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import AddButton from '../../../partials/tableCustomButtons/AddButton';
import can from '../../../utils/can';
import createSubject from '../../../utils/createSubject';
import { actions as editCdrHookReduxActions } from '../../../modals/editCdrHook/reducers';
import RefreshButton from '../../../partials/tableCustomButtons/RefreshButton';

const CdrHooksTable = ({
  cdrHooks,
  columns,
  setColumns,
  getCdrHooks,
  isLoading,
  connectClientId,
  clientId,
  openEditCrdHook
}) => {
  const [filtering, setFiltering] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    getCdrHooks(connectClientId);
    // eslint-disable-next-line
  }, [connectClientId]);

  useEffect(() => {
    if (refresh) {
      getCdrHooks(connectClientId);
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns();
  }, []);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'CdrHooks-1',
    setColumns,
    tableDefaultColumns,
    language
  });

  const actions = useMemo(
    () => [
      {
        icon: () => <FilterButton />,
        onClick: () => {
          setFiltering(!filtering);
        },
        isFreeAction: true,
        tooltip: translate('BUTTON.FILTER')
      },
      {
        icon: () => <AddButton buttonText="CDR_HOOKS.ADD_CDR_HOOK" />,
        onClick: () => openEditCrdHook(),
        isFreeAction: true,
        hidden: !can(
          'create',
          createSubject('Mitel::CdrHook', { client_id: Number(clientId) })
        )
          ? true
          : false
      },
      {
        icon: () => <RefreshButton tableHeader={true} />,
        onClick: () => setRefresh(true),
        isFreeAction: true
      }
    ],
    // eslint-disable-next-line
    [filtering]
  );

  const options = useMemo(() => {
    return {
      ...mapTableOptions({ exportData: false }),
      selection: false,
      filtering,
      pageSize
    };
  }, [filtering, pageSize]);

  return (
    <>
      <MaterialTable
        title={
          <CustomTableTitle
            text="MENU.CDR_HOOKS"
            isLoading={isLoading}
            icon={<i className="fas fa-spider-web"></i>}
          />
        }
        icons={customTableIcons}
        actions={actions}
        columns={columns}
        data={cdrHooks}
        options={options}
        localization={localization}
        onChangeColumnHidden={onChangeColumnHidden}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
};

const mapStatesToProps = ({ cdrHooks, loader, selectClient }) => {
  return {
    ...cdrHooks,
    isLoading: loader.isSpinVisible,
    connectClientId: selectClient.selectedClient?.connect30_domain,
    clientId: Number(selectClient.selectedClient.id)
  };
};

const mapDispatchToProps = {
  openEditCrdHook: editCdrHookReduxActions.openEditCdrHook,
  getCdrHooks,
  setColumns: reduxActions.setColumns
};

export default flowRight(connect(mapStatesToProps, mapDispatchToProps))(
  CdrHooksTable
);
